import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbSize, AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import * as AbHooks from "abreact/utils/AbHooks";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import * as React from "react";

/**
 * The properties for an **AbIntegerInput**.
 */

const getErrorString = (isValid, value, min, max) => {
  if (!isValid || typeof value !== "number") return AbReactResources.integerInput_invalidInteger_format();
  let returnString = AbReactResources.integerInput_invalidInteger_range();
  if (value < Number.MIN_SAFE_INTEGER && min === Number.MIN_SAFE_INTEGER) returnString += `\n${AbReactResources.invalidValue_number_tooSmall()}`;
  if (value > Number.MAX_SAFE_INTEGER && max === Number.MAX_SAFE_INTEGER) returnString += `\n${AbReactResources.invalidValue_number_tooBig()}`;
  if (min > Number.MIN_SAFE_INTEGER) returnString += `\n${AbReactResources.invalidValue_greaterThan(min.toString())}`;
  if (max < Number.MAX_SAFE_INTEGER) returnString += `\n${AbReactResources.invalidValue_lessThan(max.toString())}`;
  return returnString;
};

/**
 * The **AbIntegerInput** component allows the user to enter an integer.
 */
export function AbIntegerInput(props) {
  const contextSize = React.useContext(AbSizeContext);
  const min = Math.max(props.min ?? Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER);
  const max = Math.min(props.max ?? Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);
  const isInRange = n => n >= min && n <= max;
  const uniqueId = AbHooks.useUniqueId("ab-integer-input");
  const [textIsValid, setTextIsValid] = React.useState(true);
  const [inRange, setInRange] = React.useState(props.value ? isInRange(props.value) : true);
  const [interacted, setInteracted] = React.useState(false);
  const [numberInputValue, setNumberInputValue] = React.useState(integerToString(props.value));
  const timeoutRef = AbHooks.useErrorTimeout(() => setInteracted(false));
  const inputRef = AbHooks.useInputRef(props);
  const {
    disabled,
    tabIndex,
    id,
    className,
    style,
    ...inputProps
  } = ReactUtils.inputProps("ab-integer-input", props, ["abui", "ab-single-line", props.size ?? contextSize ?? AbSize.MEDIUM]);
  // Manually remove data-test-id because deconstruction syntax can't.
  delete inputProps["data-test-id"];
  const inputClassName = ReactUtils.classNamesString(["ab-default-input-width", "ab-integer-input-input", "ab-font-feature-tabular-numbers", "abui", disabled && "ab-disabled", !interacted && (!textIsValid || !inRange) && "ab-input-error"]);
  const onChange = newValueString => {
    const newValue = newValueString === undefined ? undefined : stringToInteger(newValueString);
    if ((typeof newValue === "number" || newValue === undefined) && newValue !== props.value) props.onChange(newValue);
  };
  AbHooks.useOnValueChange(() => {
    const currentStringValue = stringToInteger(numberInputValue);
    if (props.value === undefined && (ClassUtils.isInstanceOf(currentStringValue, Error) || currentStringValue === undefined || !isInRange(currentStringValue))) {
      return;
    }
    if (currentStringValue !== props.value) setNumberInputValue(integerToString(props.value));
    setTextIsValid(true);
    setInRange(props.value ? isInRange(props.value) : true);
  }, props.value);
  AbHooks.useOnValuesChange(() => {
    const currentStringValue = stringToInteger(numberInputValue);
    if (ClassUtils.isInstanceOf(currentStringValue, Error) || currentStringValue === undefined) setInRange(props.value ? isInRange(props.value) : true);else setInRange(isInRange(currentStringValue));
  }, [props.min, props.max, numberInputValue]);
  const keyboardHandler = e => {
    switch (e.key) {
      case KeyValue.ENTER:
        props.onEnter?.();
        setInteracted(false);
        if (textIsValid && inRange) setNumberInputValue(integerToString(props.value));
        return;
      case KeyValue.ESCAPE:
        props.onEscape?.();
        return;
      default:
        if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return; // Ignore non-character keys
        if (e.key === "-") {
          if (props.min !== undefined && props.min >= 0) {
            e.preventDefault();
            e.stopPropagation();
          }
          return;
        }
        if (!/[-+\deE]/.test(e.key))
          // Ignore invalid characters
          {
            e.preventDefault();
            e.stopPropagation();
          }
    }
  };

  // Note: there is a Chrome bug where disabled React controls do not fire mouse leave events.
  // To work around this, we don't disable inputs with tooltips explicitly, but instead remove the
  // focus and onClick handling, and visually display the input as disabled.
  //
  // One downside to this approach is that for screen readers and other accessibility tools,
  // the input does not appear to be disabled. This may be something we have to address
  // in the future.
  //
  // See: https://jira.abinitio.com/browse/CAT-1270 and https://github.com/facebook/react/issues/4251
  const useFakeDisabling = disabled && props.tooltip;
  const inner = /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: props.tooltip
  }, /*#__PURE__*/React.createElement("div", {
    style: style,
    className: className,
    id: id,
    "data-test-id": props.dataTestId
  }, /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
    className: inputClassName,
    id: uniqueId,
    type: "number",
    step: props.step ?? 1,
    value: numberInputValue,
    min: min,
    max: max,
    readOnly: props.disabled,
    title: "" // Prevents browser default from interfering
    ,
    placeholder: disabled ? undefined : props.placeholder,
    disabled: useFakeDisabling ? false : disabled,
    tabIndex: useFakeDisabling ? -1 : tabIndex,
    onInput: disabled ? undefined : e => {
      const target = e.target;
      const validity = target.validity;
      setInRange(!validity.rangeOverflow && !validity.rangeUnderflow);
      setTextIsValid(!validity.badInput);
      const valid = !validity.badInput && !validity.rangeUnderflow && !validity.rangeOverflow;
      if (!interacted && (!textIsValid || !inRange)) {
        setInteracted(valid); // If error is already showing, only set interacted if valid
      } else {
        setInteracted(true);
      }
      timeoutRef.start();
      if (valid) onChange(target.value);else onChange(undefined);
    },
    onChange: disabled ? undefined : e => {
      setNumberInputValue(e.target.value);
    },
    onBlur: () => {
      props.onBlur?.();
      setInteracted(false);
      if (textIsValid && inRange) setNumberInputValue(integerToString(props.value));
    },
    onFocus: disabled ? undefined : props.onFocus,
    onKeyDown: disabled ? undefined : keyboardHandler,
    ref: inputRef,
    onDragOver: props.onDragOver
  })), !interacted && (!textIsValid || !inRange) && /*#__PURE__*/React.createElement(AbIcon, {
    image: "absdk:error",
    className: "ab-input-error-icon",
    tooltip: getErrorString(textIsValid, stringToInteger(numberInputValue), min, max)
  })));
  return sizeProviderWrapper(inner, props, contextSize);
}
function integerToString(number) {
  if (number === undefined || !Number.isInteger(number)) return "";
  return NumberUtils.formatValue(number, -1, false);
}
const stringToInteger = s => {
  if (s.length === 0) return undefined;
  const number = Number(s);
  return !Number.isInteger(number) ? new Error("Number is not an integer") : number;
};
//# sourceMappingURL=AbIntegerInput.js.map
